import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiCheckCircle, HiOutlineChatBubbleLeftRight, HiChevronDown, HiChevronUp, HiArrowUpRight } from 'react-icons/hi2';
import Swal from 'sweetalert2';
import { saveEnquiry } from '../data/enquiriesData';
import {
  getDivisionCategories,
  getDivisionProducts
} from '../data/divisionProducts';

const staticCategories = [
  { name: 'Residential Solar', desc: 'On-grid and hybrid rooftop systems for homes and bungalows.' },
  { name: 'Commercial Solar', desc: 'Scalable solar plants for offices, malls, and commercial complexes.' },
  { name: 'Industrial Solar', desc: 'High-capacity ground-mount and rooftop systems for factories.' },
  { name: 'EPC Services', desc: 'End-to-end Engineering, Procurement & Construction services.' },
  { name: 'Operation & Maintenance', desc: 'Scheduled AMC, panel cleaning, inverter servicing, and reporting.' },
];

const benefits = [
  { title: 'Zero Electricity Bills', desc: 'Generate your own power and eliminate monthly electricity costs.' },
  { title: 'Government Subsidies', desc: 'Eligible for MNRE / MSEDCL subsidies and net metering credits.' },
  { title: 'Low Maintenance', desc: 'No moving parts means minimal service requirements for 25+ years.' },
  { title: 'Carbon Neutral', desc: 'Reduce your carbon footprint and achieve sustainability targets.' },
  { title: 'High ROI', desc: 'Typical payback period of 4–6 years with 25+ year system life.' },
  { title: 'Grid Independence', desc: 'Hybrid systems with battery ensure power during grid outages.' },
];

const faqs = [
  { q: 'What is the minimum area required for a rooftop solar installation?', a: 'Approximately 100 sq ft per kW of capacity. A 5 kW system needs around 500 sq ft of shadow-free roof space.' },
  { q: 'Do you handle MSEDCL net metering approval?', a: 'Yes. We manage the complete net metering application, MSEDCL inspection coordination, and commissioning documentation.' },
  { q: 'What is the warranty on solar panels?', a: 'We supply panels with 25-year linear power output warranty and 10-year product warranty. Inverters carry a 5-year manufacturer warranty.' },
  { q: 'How long does installation take?', a: 'Residential systems (up to 10 kW) are typically completed in 2–3 working days. Commercial installations depend on scale, usually 1–2 weeks.' },
  { q: 'Do you offer AMC for solar plants?', a: 'Yes. We provide Annual Maintenance Contracts covering panel cleaning, inverter inspection, wiring checks, and performance reporting.' },
];

export default function SolarSolutions() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', city: '', capacity: '', message: '' });

  const loadData = async () => {
    const cats = await getDivisionCategories('solar');
    const prods = await getDivisionProducts('solar');
    setCategories(cats);
    setProducts(prods);
    if (cats.length > 0) {
      setActiveTab(prev => {
        if (prev && cats.some(c => c.name === prev)) return prev;
        return cats[0].name;
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, []);

  const handleChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneDigits = formData.phone.trim();
    if (!/^\d{10}$/.test(phoneDigits)) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Please enter a valid 10-digit number.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      Swal.fire({
        title: 'Invalid Email Address',
        text: 'Please enter a valid email address.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const newEnquiry = {
      id: `enq-${Date.now()}`,
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      city: formData.city,
      message: `Solar installation query. Proposed capacity: ${formData.capacity}. Message: ${formData.message}`,
      type: 'solar',
      status: 'pending',
      date: new Date().toISOString().replace('T', ' ').slice(0, 16)
    };

    try {
      await saveEnquiry(newEnquiry, true);
      Swal.fire({ title: 'Enquiry Received!', text: `Thank you, ${formData.name}. Our solar team will contact you within 24 hours.`, icon: 'success', confirmButtonColor: '#0F5A3A' });
      setFormData({ name: '', phone: '', email: '', city: '', capacity: '', message: '' });
      e.target.reset();
    } catch (err) {
      Swal.fire('Error', 'Failed to submit enquiry. Please try again.', 'error');
    }
  };

  const filteredProducts = products.filter(p => p.category === activeTab);

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* ---- Hero Banner ---- */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/Solar Solutions.png')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Green Energy Division</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Solar <span className="text-gradient-gold">Solutions</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Complete solar EPC services — from rooftop residential systems to large-scale industrial ground-mount plants. MNRE-approved, net-metering ready.
          </p>
        </div>
      </section>

      {/* Breadcrumbs */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-2 text-xs text-body-muted">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span className="text-border-dark">/</span>
          <span>Divisions</span>
          <span className="text-border-dark">/</span>
          <span className="font-semibold text-primary">Solar Solutions</span>
        </div>
      </div>

      {/* ---- Overview ---- */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <span className="eyebrow">Overview</span>
            <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading mb-6 leading-tight">
              Harness the Power of Sunlight
            </h2>
            <div className="space-y-3 text-sm text-body leading-relaxed">
              <p>Green Gusto's Solar Division delivers turnkey solar EPC services across Maharashtra, covering system design, structural mounting, panel installation, inverter commissioning, net-metering application, and long-term AMC contracts.</p>
              <p>We work with premium panel manufacturers including Waaree, Adani Solar, and Vikram Solar, ensuring the highest efficiency ratings and 25-year linear power output guarantees for every installation.</p>
            </div>
            <div className="mt-8">
              <Link to="#solar-form" onClick={() => document.getElementById('solar-form')?.scrollIntoView({ behavior: 'smooth' })} className="btn-primary text-sm">
                Get Solar Quote
                <HiArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {[
              { v: '500+', l: 'Systems Installed' },
              { v: '5 MW+', l: 'Cumulative Capacity' },
              { v: '25 Yr', l: 'Panel Warranty' },
              { v: '100%', l: 'MSEDCL Certified' }
            ].map((s, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-5 text-center shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 hover:border-accent/20">
                <span className="text-2xl font-display font-extrabold text-primary block mb-1">{s.v}</span>
                <span className="text-[10px] text-body-muted font-bold uppercase tracking-wider">{s.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Service Categories ---- */}
      <section className="bg-light-section border-y border-border py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <span className="eyebrow">Our Services</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Solar Service Categories</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {staticCategories.map((cat, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-5 shadow-card hover:shadow-card-hover hover:-translate-y-1 hover:border-primary/20 transition-all duration-300 group">
                <div className="w-6 h-0.5 bg-accent rounded-full mb-4" />
                <h4 className="text-xs font-bold text-heading uppercase tracking-wide mb-2 group-hover:text-primary transition-colors">{cat.name}</h4>
                <p className="text-[10px] text-body-muted leading-relaxed">{cat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Product Catalog ---- */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <span className="eyebrow">Product Range</span>
          <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Solar Systems by Category</h2>
        </div>
        <div className="flex flex-wrap justify-center items-center gap-3 mb-10">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.name)}
              className={`px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === cat.name
                ? 'bg-primary text-white shadow-premium'
                : 'bg-white border border-border text-body hover:border-primary/30 hover:text-primary shadow-card'
                }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {filteredProducts.map(prod => (
            <div key={prod.id} className="product-card group flex flex-col justify-between h-full min-h-[460px]">
              <div>
                <div className="h-56 bg-light-section overflow-hidden border-b border-border">
                  <img src={prod.image || '/Divisions/solar.jpg'} alt={prod.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-grow flex flex-col">
                  <span className="badge-gold text-xs mb-2 w-fit">{prod.category}</span>
                  <h3 className="text-sm font-bold text-heading mb-2 group-hover:text-primary transition-colors">{prod.name}</h3>
                  <p className="text-[11px] text-body leading-relaxed mb-4">{prod.desc}</p>
                  {/* Specs */}
                  {prod.specs && Object.keys(prod.specs).length > 0 && (
                    <div className="grid grid-cols-2 gap-2.5 mb-4">
                      {Object.entries(prod.specs).map(([k, v]) => (
                        <div key={k} className="bg-light-section rounded-lg px-3 py-2">
                          <span className="text-[9px] text-body-muted block uppercase tracking-wide truncate">{k}</span>
                          <span className="text-xs font-bold text-primary block truncate mt-0.5">{v}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 pt-0 mt-auto">
                <button
                  onClick={() => document.getElementById('solar-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="btn-primary w-full justify-center text-xs py-2.5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all"
                >
                  Enquire Now
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Benefits ---- */}
      <section className="py-24 border-t border-b border-border bg-gradient-to-b from-[#F0F5F2] to-[#F8FAF9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="eyebrow">Why Solar</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Benefits of Going Solar</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white border border-border rounded-2xl p-6 hover:-translate-y-2 hover:border-primary-light/20 shadow-card hover:shadow-card-hover transition-all duration-300 group">
                <HiCheckCircle className="w-6 h-6 text-accent mb-4 group-hover:scale-110 transition-transform duration-200" />
                <h4 className="text-sm font-bold text-heading uppercase tracking-wide mb-2 group-hover:text-primary transition-colors">{b.title}</h4>
                <p className="text-xs text-body leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- FAQ & Enquiry Form ---- */}
      <section id="solar-form" className="py-24 bg-light-section border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

          {/* FAQ (Left Side) */}
          <div className="lg:col-span-7 w-full">
            <div className="mb-8">
              <span className="eyebrow">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="faq-item">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-8 py-7 text-left gap-4"
                  >
                    <span className="text-sm md:text-base font-bold text-heading">{faq.q}</span>
                    <span className={`flex-shrink-0 transition-colors ${openFaq === i ? 'text-accent' : 'text-body-muted'}`}>
                      {openFaq === i ? <HiChevronUp className="w-4 h-4" /> : <HiChevronDown className="w-4 h-4" />}
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5 text-sm text-body leading-relaxed border-t border-border pt-4">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Enquiry Form (Right Side) */}
          <div className="lg:col-span-5 w-full">
            <div className="bg-charcoal text-white border border-white/5 rounded-3xl p-8 md:p-10 shadow-premium relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-40 h-40 bg-accent/8 rounded-full blur-2xl" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-2">
                  <HiOutlineChatBubbleLeftRight className="w-5 h-5 text-accent" />
                  <span className="text-xs font-bold text-accent uppercase tracking-widest">Solar Enquiry Form</span>
                </div>
                <h3 className="text-xl font-display font-extrabold text-white mb-2">Tell Us Your Requirement</h3>
                <p className="text-xs text-white/60 mb-8">Our solar engineers will design the right system for you.</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[['name', 'Full Name', 'text'], ['phone', 'Phone Number', 'tel']].map(([n, p, t]) => (
                      <div key={n}>
                        <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">{p}</label>
                        <input type={t} name={n} value={formData[n]} onChange={handleChange} required placeholder={p}
                          className="input-dark" />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[['email', 'Email Address', 'email'], ['city', 'City', 'text']].map(([n, p, t]) => (
                      <div key={n}>
                        <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">{p}</label>
                        <input type={t} name={n} value={formData[n]} onChange={handleChange} required placeholder={p}
                          className="input-dark" />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Required Capacity (kW)</label>
                    <input type="text" name="capacity" value={formData.capacity} onChange={handleChange} required placeholder="e.g. 10 kW" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Message</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={3} required placeholder="Describe your requirement..."
                      className="input-dark resize-none" />
                  </div>
                  <button type="submit" className="btn-green rounded-xl w-full justify-center py-3.5 text-sm">
                    Submit Solar Enquiry
                    <HiArrowUpRight className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
